import React, { useState } from 'react';
import { InfoPair } from '../InfoPair';
import { formatNumberWithCommas } from 'utils/formatNumber';
import { TransactionNotes } from './TransactionNotes';
import { TransactionActivity } from './TransactionActivity';
import { useBlobImage } from 'hooks/useBlobImage';
import { Eye, EyeOff } from 'lucide-react';
import { formatDateToISO8601 } from 'utils/formatDateToISO8601';

export const TransactionDrawerDetails = ({ transaction }) => {
  const userPhoto = useBlobImage(transaction?.userPhoto);
  const networkPhoto = useBlobImage(transaction?.networkIcon);
  const [showTxRef, setShowTxRef] = useState(false);

  const fullName = [
    transaction?.userFirstName,
    transaction?.userLastName,
    transaction?.firstName,
    transaction?.lastName,
  ]
    .filter(Boolean)
    .join(' ');

  const initials = fullName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

  return (
    <div className="space-y-6">
      {/* Main Transaction Card */}
      <div className="bg-white/10 dark:bg-white/5 backdrop-blur-md shadow-md border border-white/10 rounded-2xl overflow-hidden">
        <div className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-4 p-6 relative">
            <div className="relative w-14 h-14">
              {userPhoto ? (
                <img
                  src={userPhoto}
                  alt="Avatar"
                  className="w-14 h-14 rounded-full object-cover border border-white/20 shadow-md"
                />
              ) : (
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white/20 text-white font-semibold text-lg border border-white/20 shadow-md">
                  {initials}
                </div>
              )}
              {networkPhoto && (
                <img
                  src={networkPhoto}
                  alt="Network"
                  className="absolute bottom-[-4px] right-[-4px] w-6 h-6 rounded-full border border-white shadow-lg animate-float-slow"
                />
              )}
            </div>

            <div className="flex flex-col">
              <span className="text-xs text-white/80 bg-white/10 border border-white/20 px-2 py-0.5 rounded-full w-fit divide-x">
                {transaction?.transactionType}
              </span>
              <span className="text-lg font-semibold text-white">
                {fullName}
              </span>
              <div className="mt-1">
                {!showTxRef ? (
                  <button
                    onClick={() => setShowTxRef(true)}
                    className="text-xs text-white/60 hover:text-white underline flex items-center gap-1"
                  >
                    <Eye className="w-4 h-4" /> View Transaction Reference
                  </button>
                ) : (
                  <button
                    onClick={() => setShowTxRef(false)}
                    type="button"
                    className="text-xs text-white/80 flex items-center gap-1"
                  >
                    <EyeOff className="w-4 h-4" /> {transaction?.tx_ref}
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="mr-12 flex flex-col items-end">
            <div className="bg-gray-100/10 text-white rounded-full text-xs text-center p-1 capitalize px-2 w-fit mb-2">
              {transaction.status}
            </div>
            <div className="text-xs">
              {formatDateToISO8601(transaction.createdAt)}
            </div>
          </div>
        </div>

        <hr className="border-t border-white/10" />

        <div className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 gap-4 divide-white/10">
          <div className="p-4 break-inside-avoid">
            {transaction?.accountNumber && (
              <InfoPair label="Account No" value={transaction?.accountNumber} />
            )}
            {transaction?.phone_card_no && (
              <InfoPair label="Phone No" value={transaction?.phone_card_no} />
            )}
          </div>
          <div className="p-4 break-inside-avoid">
            <InfoPair label="Network" value={transaction?.network} />
          </div>
          {transaction?.transactionMethod && (
            <div className="p-4 break-inside-avoid">
              <InfoPair
                label="Method"
                value={transaction?.transactionMethod?.toUpperCase()}
              />
            </div>
          )}
          <div className="p-4 col-span-2 break-inside-avoid">
            <InfoPair
              label="Amount"
              value={`${transaction.currency} ${formatNumberWithCommas(
                transaction.amount
              )}`}
            />
          </div>
          <div className="p-4 col-span-2 break-inside-avoid">
            <InfoPair
              label="Fee"
              value={`${transaction.currency} ${formatNumberWithCommas(
                transaction.fee
              )}`}
            />
          </div>
        </div>
        {transaction?.bankAccountName && (
          <>
            <hr className="border-t border-white/10" />
            <div className="flex items-center mt-4 justify-between mb-4">
              <div className="flex items-center gap-2">
                <h2 className="text-lg px-4 font-semibold text-gray-100">
                  Bank details
                </h2>
              </div>
            </div>
            <div className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 gap-4 divide-white/10">
              <div className="p-4 break-inside-avoid">
                <InfoPair label="Name" value={transaction?.bankName} />
              </div>
              <div className="p-4 break-inside-avoid">
                <InfoPair label="Branch" value={transaction?.bankBranch} />
              </div>
              <div className="p-4 break-inside-avoid">
                <InfoPair
                  label="Account Name"
                  value={transaction?.bankAccountName}
                />
              </div>
              <div className="p-4 break-inside-avoid">
                <InfoPair
                  label="Account Number"
                  value={transaction?.bankAccountNumber}
                />
              </div>
            </div>
          </>
        )}
      </div>

      <TransactionActivity transaction={transaction} />
      <TransactionNotes transaction={transaction} />

      {/* Floating animation style */}
      <style jsx>{`
        .animate-float-slow {
          animation: float-slow 6s ease-in-out infinite;
        }

        @keyframes float-slow {
          0% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-3px);
          }
          100% {
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};
